import { productImageAlt } from "@/lib/imageAlt";
import { useState, useEffect } from "react";
import { Scale, X, Plus, Minus, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchProductByHandle, ShopifyProduct, formatPrice } from "@/lib/shopify";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { useCartStore } from "@/stores/cartStore";
import { toast } from "sonner";

interface ComparisonProduct {
  handle: string;
  product: ShopifyProduct['node'] | null;
}

interface ProductComparison {
  products: ComparisonProduct[];
  addProduct: (handle: string) => void;
  removeProduct: (handle: string) => void;
  clearAll: () => void;
  isOpen: boolean;
  toggleOpen: () => void;
}

export const useProductComparison = (): ProductComparison => {
  const [products, setProducts] = useState<ComparisonProduct[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const addProduct = async (handle: string) => {
    if (products.find(p => p.handle === handle)) {
      toast.error("Product already in comparison");
      return;
    }
    if (products.length >= 3) {
      toast.error("Maximum 3 products can be compared");
      return;
    }

    setProducts(prev => [...prev, { handle, product: null }]);
    
    try {
      const product = await fetchProductByHandle(handle);
      if (product) {
        setProducts(prev => prev.map(p => 
          p.handle === handle ? { ...p, product } : p
        ));
      }
    } catch {
      toast.error("Failed to load product");
      setProducts(prev => prev.filter(p => p.handle !== handle));
    }
  };

  const removeProduct = (handle: string) => {
    setProducts(prev => prev.filter(p => p.handle !== handle));
  };

  const clearAll = () => {
    setProducts([]);
    setIsOpen(false);
  };

  const toggleOpen = () => setIsOpen(!isOpen);

  return { products, addProduct, removeProduct, clearAll, isOpen, toggleOpen };
};

interface ProductComparisonPanelProps {
  comparison: ProductComparison;
}

export const ProductComparisonPanel = ({ comparison }: ProductComparisonPanelProps) => {
  const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
    dimensions: false,
    features: false,
    materials: false,
  });
  const addItem = useCartStore(state => state.addItem);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const extractDimensions = (description: string) => {
    const dimensionRegex = /(\d+(?:\.\d+)?)\s*["""'']\s*[xX×]\s*(\d+(?:\.\d+)?)\s*["""'']\s*[xX×]\s*(\d+(?:\.\d+)?)\s*["""'']/;
    const match = description.match(dimensionRegex);
    return match ? `${match[1]}" × ${match[2]}" × ${match[3]}"` : "Not specified";
  };

  const extractMaterial = (description: string) => {
    const materials = ['wood', 'metal', 'fabric', 'leather', 'glass', 'marble', 'oak', 'walnut', 'pine'];
    for (const material of materials) {
      if (description.toLowerCase().includes(material)) {
        return material.charAt(0).toUpperCase() + material.slice(1);
      }
    }
    return "Not specified";
  };

  const handleAddToCart = async (product: ShopifyProduct['node']) => {
    const variant = product.variants?.edges?.[0]?.node;
    if (!variant) return;

    const result = await addItem({
      product: { node: product } as ShopifyProduct,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity: 1,
      selectedOptions: variant.selectedOptions || [],
    });
    if (!result.ok || result.notice) return;

    toast.success("Added to cart", { 
      description: product.title,
      position: "top-center" 
    });
  };

  if (comparison.products.length === 0) return null;

  return (
    <AnimatePresence>
      {comparison.isOpen && (
        <motion.div
          initial={{ opacity: 0, y: "100%" }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: "100%" }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed inset-0 z-50 bg-background"
        >
          <div className="h-full flex flex-col">
            {/* Header */}
            <div className="border-b border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Scale className="h-5 w-5" />
                <h2 className="font-semibold text-lg">Compare Products ({comparison.products.length})</h2>
              </div>
              <Button variant="ghost" size="icon" onClick={comparison.toggleOpen}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {comparison.products.map(({ handle, product }) => (
                  <div key={handle} className="border border-border rounded-lg overflow-hidden">
                    {product ? (
                      <>
                        {/* Product Header */}
                        <div className="p-4 border-b border-border">
                          <div className="flex justify-between items-start gap-2 mb-3">
                            <Link to={`/product/${handle}`} className="flex-1">
                              <h3 className="font-semibold text-sm line-clamp-2 hover:underline">
                                {product.title}
                              </h3>
                            </Link>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => comparison.removeProduct(handle)}
                              className="h-6 w-6"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          <Link to={`/product/${handle}`}>
                            <img
                              src={product.images?.edges?.[0]?.node?.url}
                              alt={productImageAlt(product.title)}
                              className="w-full h-40 object-contain bg-muted/20 rounded mb-3"
                            />
                          </Link>
                          
                          <div className="space-y-2">
                            <p className="text-lg font-semibold text-primary">
                              {formatPrice(
                                product.priceRange.minVariantPrice.amount,
                                product.priceRange.minVariantPrice.currencyCode
                              )}
                            </p>
                            <Button
                              onClick={() => handleAddToCart(product)}
                              className="w-full rounded-full text-xs uppercase tracking-wider"
                              size="sm"
                            >
                              Add to Cart
                            </Button>
                          </div>
                        </div>

                        {/* Comparison Data */}
                        <div className="p-4 space-y-3">
                          <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                              Dimensions
                            </h4>
                            <p className="text-sm">{extractDimensions(product.description || "")}</p>
                          </div>
                          
                          <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                              Material
                            </h4>
                            <p className="text-sm">{extractMaterial(product.description || "")}</p>
                          </div>
                          
                          <div>
                            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                              Options
                            </h4>
                            <div className="flex flex-wrap gap-1">
                              {product.options?.slice(0, 2).map((option) => (
                                <Badge key={option.name} variant="secondary" className="text-xs">
                                  {option.values.length} {option.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="p-4 animate-pulse">
                        <div className="h-4 bg-muted rounded mb-2"></div>
                        <div className="h-40 bg-muted rounded mb-3"></div>
                        <div className="h-4 bg-muted rounded"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-border p-4 flex gap-2">
              <Button variant="outline" onClick={comparison.clearAll} className="flex-1">
                Clear All
              </Button>
              <Button onClick={comparison.toggleOpen} className="flex-1">
                Continue Shopping
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

interface ComparisonButtonProps {
  productHandle: string;
  comparison: ProductComparison;
}

export const ComparisonButton = ({ productHandle, comparison }: ComparisonButtonProps) => {
  const isInComparison = comparison.products.some(p => p.handle === productHandle);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => 
        isInComparison 
          ? comparison.removeProduct(productHandle)
          : comparison.addProduct(productHandle)
      }
      className={`rounded-full text-xs ${isInComparison ? 'bg-primary/10 border-primary' : ''}`}
    >
      <Scale className="h-3 w-3 mr-1" />
      {isInComparison ? 'Remove' : 'Compare'}
    </Button>
  );
};

// Floating comparison bar
interface ComparisonBarProps {
  comparison: ProductComparison;
}

export const ComparisonBar = ({ comparison }: ComparisonBarProps) => {
  if (comparison.products.length === 0) return null;

  return (
    <motion.div
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      exit={{ y: 100 }}
      className="fixed bottom-20 md:bottom-4 left-4 right-4 z-40 bg-primary text-primary-foreground rounded-lg p-3 shadow-lg"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale className="h-4 w-4" />
          <span className="text-sm font-medium">
            Compare ({comparison.products.length})
          </span>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="secondary" 
            size="sm" 
            onClick={comparison.toggleOpen}
            className="h-8 px-3 text-xs"
          >
            View Comparison
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={comparison.clearAll}
            className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </motion.div>
  );
};